import React from "react";
import PropTypes from "prop-types";
import { Badge, Button, Card, CardHeader, Table } from "@opendate-app/setlist";
import { useStaffingPermissionsContext } from "./StaffingPermissionsContext";
import { describeScope, roleGrantFor } from "./staffingPermissionLabels";

const RolePermissionsGrid = ({ onEdit }) => {
  const { permissions, roles, grants, departments, positions } = useStaffingPermissionsContext();

  const roleCell = (role, permission) => {
    const grant = roleGrantFor(grants, role.id, permission.key);
    const allowed = grant ? grant.effect === "allow" : role.defaults.includes(permission.key);
    const scope = grant && describeScope(grant, departments, positions);
    const label = allowed ? "Allowed" : "Not allowed";
    return (
      <div>
        {role.editable && permission.editable ? (
          <Button
            variant="link-color"
            size="sm"
            aria-label={`${permission.label} for ${role.name}`}
            onClick={() => onEdit({ grantee_type: "Role", grantee_id: role.id, permission: permission.key, grant })}
          >
            {label}
          </Button>
        ) : (
          <span className="d-block small font-weight-bold">{label}</span>
        )}
        <div className="d-flex align-items-center" style={{ gap: 4 }}>
          {grant ? (
            <Badge size="sm" color={grant.effect === "allow" ? "success" : "error"}>
              Custom
            </Badge>
          ) : (
            <span className="text-muted small">Default</span>
          )}
          {scope && scope !== "Every department" && <span className="small">{scope}</span>}
        </div>
      </div>
    );
  };

  const columns = [
    {
      key: "permission",
      header: "Permission",
      primary: true,
      render: (permission) => (
        <div>
          <strong className="d-block">{permission.label}</strong>
          <span className="text-muted small">{permission.description}</span>
        </div>
      ),
    },
    ...roles.map((role) => ({ key: `role-${role.id}`, header: role.name, render: (permission) => roleCell(role, permission) })),
  ];

  return (
    <Card
      padding="md"
      header={
        <CardHeader
          title="Role permissions"
          supportingText={
            roles.every((role) => role.editable)
              ? "What each role can do in Staffing. Owners can always do everything. Promoters and guests can't use Staffing."
              : "What each role can do in Staffing. Only owners and Administrators can change the Administrator role, and you can only change permissions you have yourself."
          }
        />
      }
    >
      <Table columns={columns} data={permissions} getRowKey={(permission) => permission.key} />
    </Card>
  );
};

RolePermissionsGrid.propTypes = {
  onEdit: PropTypes.func.isRequired,
};

export default RolePermissionsGrid;
